import { __, includes, negate } from 'lodash/fp';
import styled, { css } from 'styled-components';
import { createHandler } from '@mews/std';
import { ANIMATION_CURVE, ANIMATION_DURATION, getToken, Position, PositionStrict, ZIndex } from '@optimus-web/common';
import {
    StyledTransitionGroup,
    StyledTransitionGroupProps,
    TransitionGroupChildBaseProps,
    TransitionState,
} from '@optimus-web/helpers';
import { Backdrop } from '../Backdrop';
import { Openable } from '../types';
import { SidebarProps } from './SidebarProps';

interface ChildrenProps {
    opened?: boolean;
}

interface SidebarContainerElementProps {
    absolute?: boolean;
}

export const SidebarContainerElement = styled<React.ComponentType<StyledTransitionGroupProps<ChildrenProps>>>(
    StyledTransitionGroup
).withConfig<SidebarContainerElementProps>({
    shouldForwardProp: (prop, defaultValidatorFn) => !['absolute'].includes(prop) && defaultValidatorFn(prop),
})`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-width: 320px;
    position: ${(props) => (props.absolute ? 'absolute' : 'fixed')};
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    overflow: hidden;
    pointer-events: none;
    transform: translateZ(0);
    z-index: ${ZIndex.High};
    background-color: transparent;
`;

const isTransitioning = ({ transitionState }: TransitionGroupChildBaseProps) =>
    includes(transitionState, [TransitionState.ENTERING, TransitionState.EXITING]);

export const BackdropElement = styled(Backdrop)<TransitionGroupChildBaseProps & Openable>`
    transition: opacity ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};
    opacity: 0;

    ${(props) => isTransitioning(props) && 'opacity: 0;'}
    ${(props) => props.transitionState === TransitionState.ENTERED && 'opacity: .48;'}

    ${(props) =>
        props.opened &&
        css`
            pointer-events: initial;
        `}
`;

const isVertical = includes<PositionStrict>(__, [Position.Left, Position.Right]);
const isHorizontal = negate(isVertical);
const getPosition = createHandler({
    [Position.Left]: 'left: 0;',
    [Position.Right]: 'right: 0;',
    [Position.Top]: 'top: 0;',
    [Position.Bottom]: 'bottom: 0;',
});
const getTransform = createHandler({
    [Position.Left]: 'transform: translateX(-100%);',
    [Position.Right]: 'transform: translateX(100%);',
    [Position.Top]: 'transform: translateY(-100%);',
    [Position.Bottom]: 'transform: translateY(100%);',
});

type SidebarPassedProps = Pick<
    SidebarProps,
    'width' | 'opened' | 'position' | 'onClosingAnimationFinished' | 'disableScroll'
>;
type SidebarPropsWithTransitionGroup = TransitionGroupChildBaseProps & SidebarPassedProps;

export const SidebarElement = styled('div').withConfig<SidebarPropsWithTransitionGroup>({
    shouldForwardProp: (prop, defaultValidatorFn) =>
        !['onClosingAnimationFinished'].includes(prop) && defaultValidatorFn(prop),
})`
    ${(props) =>
        isVertical(props.position) &&
        `
        min-width: 320px;
        width: ${props.width};
        height: 100%;
        flex-direction: column;
        overflow-y: ${props.disableScroll ? 'hidden' : 'auto'};
    `}
    ${(props) =>
        isHorizontal(props.position) &&
        `
        width: 100%;
        flex-direction: row;
        overflow-x: ${props.disableScroll ? 'hidden' : 'auto'};
    `}
    -webkit-overflow-scrolling: touch;
    ${(props) => getPosition(props.position)}
    position: absolute;
    pointer-events: initial;
    display: flex;
    background-color: ${getToken('backgroundStaticRaised')};
    transition: transform ${ANIMATION_DURATION.default}ms ${ANIMATION_CURVE.default};
    ${(props) => isTransitioning(props) && getTransform(props.position)}
    ${(props) => props.transitionState === TransitionState.ENTERED && 'transform: translateX(0);'}
    ${(props) =>
        (props.opened || props.transitionState !== TransitionState.EXITED) &&
        css`
            box-shadow: ${getToken('shadow300')};
        `}
    ${(props) => {
        if (props.onClosingAnimationFinished && !props.opened && props.transitionState === TransitionState.EXITED) {
            props.onClosingAnimationFinished();
        }
        return null;
    }}
    box-sizing: border-box;
`;
